import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Modal as LibModal,
  ModalHeader as LibModalHeader,
  ModalContent as LibModalContent,
  ModalFooter as LibModalFooter
} from 'qampo-ui-library';
import { useScrollLock } from '../../hooks/useScrollLock';
import { MODAL_ESCAPE_LAYER_ATTRIBUTE } from '../../constants/modalConstants';

let openModalCount = 0;

const surfaceBg: React.CSSProperties = {
  background: 'var(--color-surface-primary)'
};

// Enter confirms the modal, but only where Enter is not already the focused
// control's own key. A radio or checkbox is not typing, and treating it as such
// left Enter working everywhere in a dialog except the control the user was most
// likely holding focus on.
const NON_TEXT_INPUT_TYPES = new Set(['radio', 'checkbox']);

// Controls that activate on Enter themselves. This listener runs at the document
// in the capture phase and both stops propagation and preventDefaults, so without
// this check Enter on Cancel never reaches Cancel and fires confirm in its place.
// In a dialog whose confirm is destructive that turns the safe button into the
// dangerous one.
const SELF_ACTIVATING_SELECTOR = 'button, [role="button"], a[href], summary';

function ownsEnterKey(target: HTMLElement): boolean {
  if (target.closest(SELF_ACTIVATING_SELECTOR)) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  return !NON_TEXT_INPUT_TYPES.has((target as HTMLInputElement).type);
}

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  onConfirm?: () => void;
}

export const Modal = ({
  title,
  onClose,
  children,
  footer,
  width = 820,
  style,
  headerStyle,
  contentStyle,
  onConfirm
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useScrollLock(true, modalRef);

  // `aria-modal` tells assistive tech to confine itself to the dialog, so focus
  // has to actually be in there or a screen reader is left inside a region it
  // considers inert. The container takes focus rather than the first control,
  // which would presume which one the user wants. On close, focus goes back to
  // whatever opened the dialog, so a keyboard user does not restart at the top
  // of the page.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    modalRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    openModalCount++;
    if (openModalCount > 1) {
      console.error(
        `Multiple modals open simultaneously (${openModalCount}). Only one modal should be visible at a time.`
      );
    }
    return () => { openModalCount--; };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // An open nested popup claims Escape for itself (it closes and returns
        // focus); the modal closes only on a press no layer has claimed.
        if (document.querySelector(`[${MODAL_ESCAPE_LAYER_ATTRIBUTE}]`)) return;
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Enter' && onConfirm) {
        const target = e.target as HTMLElement;
        // Scoped to the dialog. `aria-modal` is declared but Tab is not trapped,
        // so focus can still reach the page behind; confirming a dialog from a
        // control the user cannot even see is never what they meant.
        if (!modalRef.current?.contains(target)) return;
        if (ownsEnterKey(target)) return;
        e.stopPropagation();
        e.preventDefault();
        onConfirm();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, onConfirm]);

  // The library modal renders a styled box and nothing else, so assistive tech
  // was told none of this is a dialog. Labelled from `title` rather than
  // aria-labelledby because the library header owns its own markup and exposes
  // no id to point at. NOSONAR below: S6819 wants a native <dialog>, whose
  // showModal()-driven focus containment would fight the manual focus and
  // Escape handling above.
  return createPortal(
    <div ref={modalRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}> {/* NOSONAR */}
      <LibModal width={String(width)} styles={{ modal: { ...surfaceBg, ...style } }}>
        <LibModalHeader
          modalHeaderTitle={title}
          onCloseModal={onClose}
          styles={{ root: { ...surfaceBg, ...headerStyle } }}
        />
        <LibModalContent styles={{ root: { padding: 0, gap: 'var(--spacing-sm)', ...contentStyle } }}>
          {children}
        </LibModalContent>
        {footer && <LibModalFooter styles={{ root: surfaceBg }}>{footer}</LibModalFooter>}
      </LibModal>
    </div>,
    document.body
  );
};
